/**
 * B703 (a) — the member portal's RENEW door, on the buyer's own
 * `membership.renew` (CUSTOMER, owner-fenced). The press sends what the face
 * shows — the term it renews and the total the door charges — and the
 * portal's own address to come back to. With no card on file the door's
 * first answer is the payment provider's card page; the provider sends the
 * buyer back to `?renew=<member>`, where the same door charges the saved
 * card (one ask per return; the address is cleaned first so a reload never
 * asks again — and the door answers a term that already moved without
 * charging anyway). A renewed term is said in words and the reads are taken
 * again; a refusal is the door's sentence.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api.js";
import { meMembershipUrl } from "../lib/me.js";
import { RENEW_NOT_CONFIRMED, membershipFaces, renewAnswer, renewReturnUrl, renewedWords, type PortalMember, type PortalPlan } from "../lib/meMembership.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

export interface MemberRenewing {
  readonly memberNumber: string;
  readonly busy: boolean;
  readonly banner?: { readonly tone: "info" | "success" | "danger"; readonly text: string };
}

const CARD_NOT_SAVED = "Your card wasn't saved, so nothing was charged. Press Renew again to add it.";

export function useMemberRenew(opts: {
  readonly caller: Caller | undefined;
  readonly tenantId: string | undefined;
  readonly members: readonly PortalMember[] | undefined;
  readonly plans: Readonly<Record<string, PortalPlan | undefined>>;
  readonly timeZone: string | undefined;
  readonly origin: string;
  /** The member the card page sent the buyer back to finish (`&renew=`). */
  readonly returning: string | undefined;
  readonly go: (url: string) => void;
  readonly clearAddress: ((path: string) => void) | undefined;
  readonly reread: () => void;
  readonly onSignedOut: () => void;
}): { readonly renewing: MemberRenewing | undefined; readonly renew: ((memberNumber: string) => void) | undefined } {
  const { caller, tenantId, members, plans, timeZone } = opts;
  const [renewing, setRenewing] = useState<MemberRenewing | undefined>(undefined);

  const ask = useCallback(
    async (memberNumber: string, from: "press" | "return"): Promise<void> => {
      if (caller === undefined || tenantId === undefined || members === undefined) return;
      const door = membershipFaces(members, plans, { timeZone }).find((f) => f.memberNumber === memberNumber)?.renewal?.renew;
      if (door === undefined) {
        setRenewing({ memberNumber, busy: false, banner: { tone: "danger", text: RENEW_NOT_CONFIRMED } });
        return;
      }
      setRenewing({ memberNumber, busy: true });
      let answer;
      try {
        answer = renewAnswer(
          await caller.call("membership.renew", { memberNumber, termEnd: door.termEnd, amount: door.amount, returnUrl: renewReturnUrl(opts.origin, tenantId, memberNumber) }),
        );
      } catch (e: unknown) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          opts.onSignedOut();
          return;
        }
        const said = e instanceof ApiError ? (e.code === "REFUSED" ? e.message : RENEW_NOT_CONFIRMED) : e instanceof Error && e.message.trim() !== "" ? e.message : RENEW_NOT_CONFIRMED;
        setRenewing({ memberNumber, busy: false, banner: { tone: "danger", text: said } });
        return;
      }
      if (answer.kind === "renewed") {
        setRenewing({ memberNumber, busy: false, banner: { tone: "success", text: renewedWords(answer.termEnd, timeZone) } });
        opts.reread();
        return;
      }
      if (answer.kind === "card" && answer.refused === undefined && from === "press") {
        // The provider's own card page; the busy press stands until the tab leaves.
        opts.go(answer.setupUrl);
        return;
      }
      const text = answer.kind === "refused" ? answer.message : (answer.refused ?? CARD_NOT_SAVED);
      setRenewing({ memberNumber, busy: false, banner: { tone: answer.kind === "card" && answer.refused === undefined ? "info" : "danger", text } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seams are fixed for the page's life
    [caller, tenantId, members, plans, timeZone],
  );

  // The way back from the card page: finish the renewal the address names, once.
  const finished = useRef(false);
  useEffect(() => {
    if (opts.returning === undefined || members === undefined || tenantId === undefined || finished.current) return;
    finished.current = true;
    opts.clearAddress?.(meMembershipUrl(tenantId));
    void ask(opts.returning, "return");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the return is read once
  }, [members, tenantId, ask]);

  const renew = useCallback((memberNumber: string) => void ask(memberNumber, "press"), [ask]);
  return { renewing, renew: caller === undefined ? undefined : renew };
}
